import { useEffect, useState } from 'react';
import { useCancellablePromise } from 'helpers/promiseHandler';
import { Fetch } from '../helpers/fetchWrapper';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};
const merchantToolService = {
  _url: `${process.env.REACT_APP_COMMON_API_URL}/merchant-tool`,
  GetBusinessFees(mid) {
    const [fees, setFees] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getBusinessFees(mid))
        .then(res => {
          setFees(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [mid]);
    return {
      fees_loading: status === STATUS.LOADING,
      fees_error: status === STATUS.ERROR ? status : '',
      fees_data: fees,
    };
  },
  GetMerchantFees(searchQuery) {
    const [merchantFees, setMerchantFees] = useState({
      fees: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getMerchantFees(searchQuery))
        .then(res => {
          setMerchantFees(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [searchQuery]);
    return {
      merchant_loading: status === STATUS.LOADING,
      merchant_error: status === STATUS.ERROR ? status : '',
      merchant_data: merchantFees,
    };
  },
  async uploadTransactions(transactions) {
    const res = await Fetch.post(`${this._url}/upload-transaction`, transactions);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async uploadSettlements(settlements) {
    const res = await Fetch.post(`${this._url}/upload-settlements`, settlements);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async uploadSettlementFees(settlementFees) {
    const res = await Fetch.post(`${this._url}/upload-settlement-fees`, settlementFees);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async uploadFees(fees) {
    const res = await Fetch.post(`${this._url}/upload-fees`, fees);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async uploadMerchantFundingSummary(fundingSummary) {
    const res = await Fetch.post(`${this._url}/upload-funding-summary`, fundingSummary);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getBusinessFees(mid) {
    let res = await Fetch.get(`${this._url}/get-business-fees?mid=${mid}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getMerchantToolToken(_) {
    const res = await Fetch.post(`${this._url}/get-token`, _);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async remakeStatements({ year, month }) {
    const res = await Fetch.get(`${this._url}/generate-all-statements?year=${year}&month=${month}`);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getMerchantFees({ page = 1, perPage = 10, searchText = '' } = {}) {
    const res = await Fetch.get(`${this._url}/merchant-fees?page=${page}&perPage=${perPage}&searchText=${searchText}`);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return {
        fees: data.items,
        totalItems: data.totalItems,
      };
    }

    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  GetDuplicates(searchQuery, fetch) {
    const [duplicates, setDuplicates] = useState({
      duplicates: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getDuplicates(searchQuery))
        .then(res => {
          setDuplicates(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [
      searchQuery?.searchText,
      searchQuery?.page,
      searchQuery?.pageSize,
      searchQuery?.filterStatus,
      searchQuery?.startDate,
      searchQuery?.endDate,
      searchQuery?.filterMerchant,
      fetch,
    ]);
    return {
      duplicates_loading: status === STATUS.LOADING,
      duplicates_error: status === STATUS.ERROR ? status : '',
      duplicates_data: duplicates,
    };
  },
  GetDuplicateSummary() {
    const [summary, setSummary] = useState({});
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getDuplicateSummary())
        .then(res => {
          setSummary(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, []);
    return {
      summary_loading: status === STATUS.LOADING,
      summary_error: status === STATUS.ERROR ? status : '',
      summary_data: summary,
    };
  },
  async getDuplicates({
    page = 1,
    pageSize = 10,
    filterStatus = '',
    filterMerchant = '',
    dateFrom = '',
    dateTo = '',
    searchText = '',
  } = {}) {
    const query = new URLSearchParams({
      page,
      limit: pageSize, // API uses 'limit' for pageSize
      ...(filterStatus && { filterStatus }),
      ...(filterMerchant && { filterMerchant }),
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
      ...(searchText && { searchText }),
    }).toString();
    const res = await Fetch.get(`${this._url}/duplicates?${query}`);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return {
        duplicates: data.data || [],
        totalItems: data.pagination?.total || 0,
      };
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getDuplicateSummary() {
    const res = await Fetch.get(`${this._url}/duplicates/summary`);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getDuplicateById(id) {
    const res = await Fetch.get(`${this._url}/duplicates/${id}`);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data.data.duplicateGroup;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  GetDuplicateById(id) {
    const [duplicate, setDuplicate] = useState(null);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!id) return;
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getDuplicateById(id))
        .then(res => {
          setDuplicate(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [id]);
    return {
      duplicate_loading: status === STATUS.LOADING,
      duplicate_error: status === STATUS.ERROR ? status : '',
      duplicate_data: duplicate,
    };
  },
  async updateDuplicateStatus(id, statusData) {
    const res = await Fetch.put(`${this._url}/duplicates/${id}/status`, statusData);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async duplicateMerchantFiltersOptions() {
    const res = await Fetch.get(`${this._url}/duplicates-options`);
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async deleteDuplicate(id) {
    const res = await Fetch.delete(`${this._url}/duplicates/${id}`, {});
    if (res.status >= 200 && res.status < 300) {
      const data = await res.json();
      return data;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
};
export default merchantToolService;
